/* ════════════════════════════════════════════════════════════════════════════
   v5.0 — NAVIGATION: Programme Management Plan overview + hash routing.
   Two views toggle via the `view-overview` class on <body>:
     • overview  (#overviewView) — the landing grid of project cards
     • project   (#projectView)  — the full per-project tabbed app
   Hash routes: #/overview and #/project/<id> (id = the stable project id).
   hashchange drives everything, so browser Back/Forward and deep links/bookmarks
   all work. Switching never loses edits — one in-memory DB, and setActiveProject
   just re-points the live containers at the chosen project.
   ════════════════════════════════════════════════════════════════════════════ */
function gotoOverview() { if (_progressPermitRoute('#/overview')) location.hash = '#/overview'; }
function gotoPortfolio() { if (_progressPermitRoute('#/portfolio')) location.hash = '#/portfolio'; }   /* v10.5 (W2) */
function gotoProject(id) {
  const target = '#/project/' + encodeURIComponent(id);
  if (_progressPermitRoute(target)) location.hash = target;
}

function showOverview() {
  if (!_progressPermitRoute('#/overview')) return false;
  _pfResetExpansion();
  _disposeActionLabels();
  _settleStakeholderCards();
  _closeStakeholderMenus();
  _progressHelpClose();
  _projReturn = null;  /* v8.3.0: any project opened from the overview returns to the overview */
  _reorderUiBind('project', activeProgrammeId);
  renderOverview();
  document.body.classList.add('view-overview');
  document.body.classList.remove('view-focus');
  document.body.classList.remove('view-portfolio');   /* v10.5 (W2) */
  _renderLocationRail('programme');
  /* ⚠ v16.5 (c): AFTER the body class. showOverview calls renderOverview() FIRST, so the sync
     inside it read the PREVIOUS view's level and offered the wrong placeholder. The same reason
     _refreshFileAlerts is called here rather than earlier. */
  _hdrSearchSync();
  _lbSetSwitch('accounts');
  _refreshFileAlerts();   /* v11.1 (W2): after the body class, which gates the strip */
  const dd = document.getElementById('projSwitcher'); if (dd) dd.hidden = true;
  /* v7.0.1: the overview is cross-project — clear any project name left in the
     <title> by setActiveProject so the browser tab reflects the current view.
     v10.5 (W1): the view now has a name of its own — the programme's. */
  document.title = _progDocTitle();
  return true;
}
function showProject() {
  const target = activeProjectId == null ? '#/project/' : '#/project/' + encodeURIComponent(activeProjectId);
  if (!_progressPermitRoute(target)) return false;
  _pfResetExpansion();
  _progressHelpClose();
  _reorderUiReset();
  if (_wbsReorderReady) _wsReorderBind(_wsActiveProjectUid() || '');
  document.body.classList.remove('view-overview');
  document.body.classList.remove('view-focus');
  document.body.classList.remove('view-portfolio');   /* v10.5 (W2) */
  /* ⚠ v16.5 (c): AFTER the body class, which is what _hdrSearchLevel reads. A project view has
     no list to filter, so the field hides - and hiding it also CLEARS it, so a filter can never
     be left applied to a list the user has navigated away from (◆c-3). */
  _hdrSearchSync();
  _renderLocationRail('project');
  _refreshFileAlerts();   /* v11.1 (W2): the level with no file furniture of its own */
  const dd = document.getElementById('projSwitcher'); if (dd) dd.hidden = true;
  if (_wbsReorderReady) _wsReorderSyncUi(_wsActiveProjectUid() || '');
  /* v6: honor a pending Focus jump — switch to the item's tab and flash the card */
  if (_focusPendingTab || _focusPendingCard) {
    const tab = _focusPendingTab, card = _focusPendingCard;
    _focusPendingTab = null; _focusPendingCard = null;
    setTimeout(() => {
      try { if (tab && typeof switchTab === 'function') switchTab(tab); } catch (e) {}
      if (card) {
        /* ⚠ v15.2: a Focus jump to an ACTION must survive the target project's own saved
           state. Two silent strandings lived here: the board view saved as active (the card
           exists but sits in the display:none list), and the project's persisted
           search/status/priority filters hiding the card (restoreUIState has just applied
           them). Both ended in a no-op with no explanation — the exact stranding v14.2 gave
           the board→list jump a toast for. gotoActionCard already owns the full remedy
           (view reset, filter diagnosis, the tell-never-silently-clear toast), so an action
           jump goes through it rather than through a second, dumber copy of it. */
        if (/^card-\d/.test(card) && typeof gotoActionCard === 'function'
            && typeof actionIdFromDisplay !== 'undefined') {
          const aid = Number(card.slice(5));
          if (isFinite(aid)) { gotoActionCard(aid); return; }
        }
        const el = document.getElementById(card);
        if (el) {
          /* v10.0: a jump target inside (or being) a folded <details> would scroll
             nowhere visible — open the enclosing section first. Benefits the
             heatmap hop (kpiSection) and Focus jumps into folded sections alike. */
          const sec = el.closest('details');
          if (sec) sec.open = true;
          if (el.tagName === 'DETAILS') el.open = true;
          try { el.scrollIntoView({ block: 'center' }); } catch (e) { if (el.scrollIntoView) el.scrollIntoView(); }
          el.classList.add('fx-flash');
          setTimeout(() => el.classList.remove('fx-flash'), 1600);
        }
      }
    }, 0);
  }
  return true;
}
function route() {
  if (!_progressPermitRoute(location.hash || '#/portfolio')) return false;
  const m = (location.hash || '').match(/^#\/project\/(.+)$/);
  if (m) {
    const id = decodeURIComponent(m[1]);
    const exists = window.__DB__ && window.__DB__.projects.some(c => c && c.id === id && !c.archived);
    if (exists) { if (!setActiveProject(id)) return false; showProject(); return true; }
    /* unknown or archived id → fall through to the overview */
  }
  if ((location.hash || '') === '#/focus') { showFocus(); return true; }
  if ((location.hash || '') === '#/portfolio') { showPortfolio(); return true; }   /* v10.5 (W2) */
  /* v11: with no programme there is nothing for the programme view to be ABOUT — it would
     render a nameless, projectless shell. Land on the portfolio, which is also where the
     one button that fixes it lives. */
  if (!activeProgrammeId || !_programmes().length) { showPortfolio(); return true; }
  /* v12.1 (W2): #/overview stays explicit — only a MISSING hash defaults, and it now
     defaults to the PORTFOLIO. Before this, boot descended into programmes[0], which is
     how a fresh launch landed inside "PMO" instead of the front door. */
  if ((location.hash || '') === '#/overview') {
    if (!activeProgrammeId || !_programmes().length) { showPortfolio(); return true; }
    showOverview(); return true;
  }
  showPortfolio();
  return true;
}

/* History (v10.0): the FIRST data outside projects[] since v8.4's serialiser was
   written was a single `__DB__.programme` branch — the model v11 replaces below.
   Still true and inherited: benefits are PROGRAMME-ONLY (Q-e), and their status pill
   uses the v8.0 _BEN_STATUS five-state vocabulary (Not set → On track → At risk →
   Off track → Realised). The module-load normalise pass remains schema-gated. */
/* ══ v11: the programme layer becomes a LIST — __DB__.programmes[] (schema 9) ═════════
   v10.0 introduced ONE programme branch; v11 makes it an array, because a single data
   file now holds the whole portfolio. Each record:
       { id, name, rag: { level, rationale }, benefits: [] }   (+ a lazy benSeq)
   ⚠ `projects[]` STAYS FLAT — every project carries a `programmeId` naming its owner.
   That is what held the blast radius to a filter, a view and one serialiser line instead
   of a reshape (Route 2, not the rejected Route 3).
   Serialised WHOLESALE as one line exactly as the singular branch was, so new keys
   inside a programme still ride along for free.
   ⚠ NO migration (◆A vetoed at the v11 lock): schema 9 refuses schema 8 exactly as it
   refuses 6 and 7. Refusing is the gate working — it is not the standing
   never-relax-without-a-migration rule being broken.
   ⚠ Everything reads the current programme through _activeProg(). Nothing outside this
   block and the serialiser touches __DB__.programmes directly. */
let activeProgrammeId = null;

function _programmes() {
  const db = window.__DB__;
  return (db && Array.isArray(db.programmes)) ? db.programmes : [];
}
function _activeProg() {
  const list = _programmes();
  if (!list.length) return null;
  return list.find(p => p && p.id === activeProgrammeId) || null;
}
/* == v17.3 (W2, F-P2-01): A RETIRED PROGRAMME ID IS NOT AVAILABLE FOR REUSE ================
   THE DEFECT. A programme's id is derived from its NAME ("Delivery" -> `delivery`), and the
   uniqueness test below was built from `_programmes()` - the LIVE list. A deletion marker is not
   in that list, so deleting "Delivery" and later creating another "Delivery" handed the newcomer
   the retired id. The old marker is still in the merged portfolio at whatever revision that
   programme reached, `_mergeBeats` prefers the higher revision, and `_normProgrammes` then
   applies the v16.8 W4 cascade - so the new programme AND every project inside it are removed,
   on every machine, after a Save that reported success. Measured by the SS17.3 QA run (M4-06).

   THE TWO CALL SITES NEED DIFFERENT SOURCES, AND THAT IS THE WHOLE DESIGN. Markers live in
   `db.programmes` until `_revAdoptBaseline` harvests them into `_revTombs`, and `_normProgrammes`
   runs BEFORE that harvest (v16.7 W3's fact). So:
     - the REPAIR call inside _normProgrammes already sees markers in `_programmes()` - nothing
       to add, and nothing is added there;
     - `createProgramme` runs long after the harvest, when `_revTombs` is the only place a
       retired id still exists - so it passes them in.

   DO NOT "SIMPLIFY" THIS BY READING `_revTombs` DIRECTLY IN HERE. v16.9 W2's L7 MEASURED that
   `_revTombs` still holds the PREVIOUS portfolio's markers while a new one is being normalised,
   and the display-number code one function away carries the same warning for the same reason. A
   global lookup at the repair site would reserve ids out of the wrong portfolio. The parameter
   exists precisely so each caller supplies what is true where IT stands.

   Projects were never exposed to this: they key on `uid` from `_newUid`, which is never reused
   (proved clean in M4-07). This is a programme-only hazard because only programmes derive their
   identity from a name. */
function _newProgrammeId(name, alsoTaken) {
  const base = String(name || 'programme').toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'programme';
  const taken = new Set(_programmes().map(p => p && p.id));
  if (Array.isArray(alsoTaken)) for (let i = 0; i < alsoTaken.length; i++) taken.add(alsoTaken[i]);
  let id = base, n = 2;
  while (taken.has(id)) id = base + '-' + (n++);
  return id;
}
/* The ids that deletion markers still hold, for the portfolio currently in memory. `_revTombs`
   is set by `_revAdoptBaseline` from the adopted portfolio and extended by each successful Save
   (`_revTombRecords(_plan)`), so from the first paint after a load it describes THIS portfolio.
   Root-level programme markers only: a marker's walk key is `programmes/<id>`, so its `path` is
   exactly 'programmes' - a project or a nested record never matches. */
function _retiredProgrammeIds() {
  const out = [];
  if (!Array.isArray(_revTombs)) return out;
  for (let i = 0; i < _revTombs.length; i++) {
    const t = _revTombs[i];
    if (t && t.path === 'programmes' && typeof t.id === 'string' && t.id) out.push(t.id);
  }
  return out;
}
function createProgramme(name) {
  const db = window.__DB__;
  if (!db || !Array.isArray(db.programmes)) return null;
  const rec = { id: _newProgrammeId(name, _retiredProgrammeIds()),
                name: (name || '').replace(/\s+/g, ' ').trim().slice(0, 80),
                rag: { level: null, rationale: '' }, benefits: [] };
  db.programmes.push(rec);
  return rec.id;
}
/* Projects belonging to the ACTIVE programme — every LISTING view goes through this.
   ⚠ Id-based lookups deliberately do NOT: ids stay unique across the whole file, and a
   deep link, an undo restore or a cross-programme reference must still resolve. */
function _progProjects(includeArchived) {
  const db = window.__DB__;
  const all = (db && Array.isArray(db.projects)) ? db.projects : [];
  const mine = all.filter(c => c && c.programmeId === activeProgrammeId);
  return includeArchived ? mine : mine.filter(c => !c.archived);
}
/* ══ v17.1 W3: PROJECT PREDECESSORS — LAZY METADATA, VISUAL PLANNING ONLY ═══════════════
   The one stored field is project.appMeta.predecessorUids. It is absent until used and
   deleted again when cleared. UIDs are immutable project identity; names and routing ids
   may change. There is deliberately no scheduling, date, blocking, RAG, gate or critical-
   path behaviour behind this field.

   Unknown UIDs are evidence, not rubbish: an incomplete Import or an as-yet-unsynchronised
   colleague can make a valid endpoint unavailable locally. They remain stored and visible
   as "Unavailable project" until the user deliberately clears them. Only an explicit
   project tombstone/delete or a confirmed cross-programme move is proof enough to prune. */
const _PRED_CHAIN_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6.3 5.1 4.8 3.6a2.6 2.6 0 0 0-3.7 3.7l2.2 2.2A2.6 2.6 0 0 0 7 9.6l.8-.8"/><path d="m9.7 10.9 1.5 1.5a2.6 2.6 0 0 0 3.7-3.7l-2.2-2.2A2.6 2.6 0 0 0 9 6.4l-.8.8"/><path d="m5.8 10.2 4.4-4.4"/></svg>';
let _predEditorProjectId = null;
let _predMapOpen = false;

function _projectPredecessorUids(project) {
  const raw = project && project.appMeta && Array.isArray(project.appMeta.predecessorUids)
    ? project.appMeta.predecessorUids : [];
  const out = [], seen = new Set();
  raw.forEach(function (uid) {
    if (typeof uid !== 'string') return;
    const clean = uid.trim();
    if (!clean || seen.has(clean)) return;
    seen.add(clean); out.push(clean);
  });
  return out;
}
function _projectByUid(uid, db) {
  const root = db || window.__DB__;
  return ((root && Array.isArray(root.projects)) ? root.projects : [])
    .find(function (p) { return p && !p.del && p.uid === uid; }) || null;
}
function _setProjectPredecessorUids(project, uids) {
  if (!project) return;
  if (!project.appMeta || typeof project.appMeta !== 'object') project.appMeta = {};
  const clean = [], seen = new Set();
  (Array.isArray(uids) ? uids : []).forEach(function (uid) {
    if (typeof uid !== 'string') return;
    uid = uid.trim();
    if (!uid || seen.has(uid)) return;
    seen.add(uid); clean.push(uid);
  });
  if (clean.length) project.appMeta.predecessorUids = clean;
  else delete project.appMeta.predecessorUids;
}
function _predecessorLabel(project, uid) {
  const found = _projectByUid(uid);
  if (!found || !project || found.programmeId !== project.programmeId)
    return 'Unavailable project (' + uid + ')';
  return (found.name || 'Untitled project') + (found.archived ? ' (archived)' : '');
}
function _predecessorSummary(project) {
  return _projectPredecessorUids(project).map(function (uid) {
    return _predecessorLabel(project, uid);
  });
}
function _predCountHtml(project) {
  const refs = _projectPredecessorUids(project);
  if (!refs.length) return '';
  const title = 'Predecessors: ' + _predecessorSummary(project).join(', ') + '. Open to review or edit.';
  return '<button class="pred-count" type="button"'
    + ' onclick="event.stopPropagation();openPredecessorEditor('
    + "'" + _jsInAttr(project.id) + "'" + ', event)"'
    + ' title="' + _escapeHtml(title) + '" aria-label="' + _escapeHtml(title) + '">'
    + _PRED_CHAIN_ICON + String(refs.length) + '</button>';
}
function _paintCrumbPredecessors(project) {
  const el = document.getElementById('crumbPredCount');
  if (!el) return;
  const refs = _projectPredecessorUids(project);
  el.hidden = !refs.length;
  if (!refs.length) { el.innerHTML = ''; el.title = ''; el.setAttribute('aria-label', ''); return; }
  const title = 'Predecessors: ' + _predecessorSummary(project).join(', ') + '. Open to review or edit.';
  el.innerHTML = _PRED_CHAIN_ICON + String(refs.length);
  el.title = title;
  el.setAttribute('aria-label', title);
}

/* Returns a plain result so the editor, compatibility probe and future Import checks all
   use one rule. Unknown UIDs are allowed to remain, but a known project in another
   programme, a self-link, a duplicate or a loop is refused. */
function validateProjectPredecessors(projectUid, selectedUids, dbArg) {
  const db = dbArg || window.__DB__;
  const target = _projectByUid(projectUid, db);
  if (!target) return { ok:false, message:'This project is no longer available.' };
  if (!Array.isArray(selectedUids))
    return { ok:false, message:'The predecessor selection could not be read.' };
  const clean = [], seen = new Set();
  for (const raw of selectedUids) {
    if (typeof raw !== 'string' || !raw.trim()) continue;
    const uid = raw.trim();
    if (seen.has(uid))
      return { ok:false, message:'Each predecessor can be selected only once.' };
    seen.add(uid); clean.push(uid);
    if (uid === projectUid)
      return { ok:false, message:'A project cannot be its own predecessor.' };
    const known = _projectByUid(uid, db);
    if (known && known.programmeId !== target.programmeId)
      return { ok:false, message:'Predecessors must belong to the same programme.' };
  }
  const projects = ((db && Array.isArray(db.projects)) ? db.projects : [])
    .filter(function (p) { return p && !p.del && typeof p.uid === 'string'; });
  const byUid = new Map(projects.map(function (p) { return [p.uid, p]; }));
  const state = new Map();
  function visit(uid) {
    if (!byUid.has(uid)) return false;
    if (state.get(uid) === 1) return true;
    if (state.get(uid) === 2) return false;
    state.set(uid, 1);
    const p = byUid.get(uid);
    const next = uid === projectUid ? clean : _projectPredecessorUids(p);
    for (const ref of next) {
      if (visit(ref)) return true;
    }
    state.set(uid, 2);
    return false;
  }
  if (visit(projectUid))
    return { ok:false, message:'That selection would create a dependency loop. Choose a different predecessor.' };
  return { ok:true, uids:clean };
}

function _predSetError(message) {
  const el = document.getElementById('predError');
  if (el) el.textContent = message || '';
}
function openPredecessorEditor(id, e) {
  if (e) e.stopPropagation();
  _closeAllMenus();
  const project = ((window.__DB__ && window.__DB__.projects) || [])
    .find(function (p) { return p && !p.del && p.id === id; });
  if (!project) return;
  _predEditorProjectId = project.id;
  const selected = new Set(_projectPredecessorUids(project));
  const eligible = ((window.__DB__ && window.__DB__.projects) || [])
    .filter(function (p) {
      return p && !p.del && p.uid !== project.uid && p.programmeId === project.programmeId;
    })
    .sort(function (a, b) {
      return Number(!!a.archived) - Number(!!b.archived)
        || (_dispNoOf(a) - _dispNoOf(b))
        || String(a.name || '').localeCompare(String(b.name || ''));
    });
  const eligibleUids = new Set(eligible.map(function (p) { return p.uid; }));
  const rows = eligible.map(function (p) {
    const label = p.name || 'Untitled project';
    const meta = (_dispId('PRJ', p) || 'Project') + (p.archived ? ' · archived' : '');
    return '<label class="pred-row">'
      + '<input class="pred-choice" type="checkbox" data-uid="' + _escapeHtml(p.uid) + '"'
      + (selected.has(p.uid) ? ' checked' : '') + ' onchange="_predSetError(\'\')">'
      + '<span class="pred-row-main"><span class="pred-row-name">' + _escapeHtml(label) + '</span>'
      + '<span class="pred-row-meta">' + _escapeHtml(meta) + '</span></span></label>';
  });
  selected.forEach(function (uid) {
    if (eligibleUids.has(uid)) return;
    rows.push('<label class="pred-row pred-row-unavailable">'
      + '<input class="pred-choice" type="checkbox" data-uid="' + _escapeHtml(uid)
      + '" checked onchange="_predSetError(\'\')">'
      + '<span class="pred-row-main"><span class="pred-row-name">Unavailable project</span>'
      + '<span class="pred-row-meta">Stored reference ' + _escapeHtml(uid)
      + ' · clear it to remove</span></span></label>');
  });
  const list = document.getElementById('predList');
  if (list) list.innerHTML = rows.length ? rows.join('')
    : '<div class="pred-map-empty">There are no other projects in this programme yet.</div>';
  const title = document.getElementById('predTitle');
  if (title) title.textContent = 'Set predecessors for “' + (project.name || 'this project') + '”';
  _predSetError('');
  const ov = document.getElementById('predOverlay');
  if (ov) ov.classList.add('open');
  setTimeout(function () {
    const first = document.querySelector('#predList .pred-choice');
    const save = document.getElementById('predSaveBtn');
    if (first) first.focus(); else if (save) save.focus();
  }, 10);
}
function closePredecessorEditor() {
  const ov = document.getElementById('predOverlay');
  if (ov) ov.classList.remove('open');
  _predEditorProjectId = null;
  _predSetError('');
}
function savePredecessors() {
  const project = ((window.__DB__ && window.__DB__.projects) || [])
    .find(function (p) { return p && !p.del && p.id === _predEditorProjectId; });
  if (!project) { closePredecessorEditor(); return; }
  const selected = Array.from(document.querySelectorAll('#predList .pred-choice:checked'))
    .map(function (el) { return el.dataset.uid || ''; });
  const verdict = validateProjectPredecessors(project.uid, selected);
  if (!verdict.ok) { _predSetError(verdict.message); return; }
  const before = JSON.stringify(_projectPredecessorUids(project));
  _setProjectPredecessorUids(project, verdict.uids);
  const changed = before !== JSON.stringify(_projectPredecessorUids(project));
  closePredecessorEditor();
  if (changed) markDirty();
  if (document.body.classList.contains('view-overview')) renderOverview();
  else _paintCrumbPredecessors(project);
}

/* Remove one proven-dead UID from every dependent project and optionally record enough
   to restore it during the existing short Undo window. */
function _pruneProjectPredecessorUid(uid, sweptOut, dbArg) {
  const db = dbArg || window.__DB__;
  if (!db || !Array.isArray(db.projects) || typeof uid !== 'string') return 0;
  let removed = 0;
  db.projects.forEach(function (p) {
    if (!p || p.del || !p.appMeta || !Array.isArray(p.appMeta.predecessorUids)) return;
    const raw = p.appMeta.predecessorUids;
    const keep = [];
    raw.forEach(function (ref, index) {
      if (ref === uid) {
        removed++;
        if (Array.isArray(sweptOut)) sweptOut.push({ projectUid:p.uid, predecessorUid:uid, index:index });
      } else keep.push(ref);
    });
    if (keep.length) p.appMeta.predecessorUids = keep;
    else delete p.appMeta.predecessorUids;
  });
  return removed;
}
function _restoreProjectPredecessorLinks(swept) {
  const rows = Array.isArray(swept)
    ? swept.slice().sort(function (a, b) { return a.index - b.index; }) : [];
  rows.forEach(function (link) {
    const p = _projectByUid(link.projectUid);
    if (!p || typeof link.predecessorUid !== 'string') return;
    if (!p.appMeta || typeof p.appMeta !== 'object') p.appMeta = {};
    const refs = Array.isArray(p.appMeta.predecessorUids) ? p.appMeta.predecessorUids : [];
    if (refs.indexOf(link.predecessorUid) !== -1) return;
    refs.splice(Math.max(0, Math.min(Number(link.index) || 0, refs.length)), 0, link.predecessorUid);
    p.appMeta.predecessorUids = refs;
  });
}
function _predecessorLinksInvalidAfterMove(projectId, toProgId) {
  const db = window.__DB__;
  const projects = (db && Array.isArray(db.projects)) ? db.projects : [];
  const moved = projects.find(function (p) { return p && !p.del && p.id === projectId; });
  if (!moved) return [];
  const links = [];
  _projectPredecessorUids(moved).forEach(function (uid) {
    const pred = _projectByUid(uid);
    if (pred && pred.programmeId !== toProgId)
      links.push({ projectUid:moved.uid, predecessorUid:uid });
  });
  projects.forEach(function (p) {
    if (!p || p.del || p.uid === moved.uid || p.programmeId === toProgId) return;
    if (_projectPredecessorUids(p).indexOf(moved.uid) !== -1)
      links.push({ projectUid:p.uid, predecessorUid:moved.uid });
  });
  return links;
}
function _removeProjectPredecessorLinks(links) {
  let removed = 0;
  (Array.isArray(links) ? links : []).forEach(function (link) {
    const p = _projectByUid(link.projectUid);
    if (!p) return;
    const before = _projectPredecessorUids(p);
    const after = before.filter(function (uid) { return uid !== link.predecessorUid; });
    if (after.length === before.length) return;
    _setProjectPredecessorUids(p, after);
    removed += before.length - after.length;
  });
  return removed;
}
function _prunePredecessorUidsFromDB(db, deadUids) {
  if (!db || !Array.isArray(db.projects) || !(deadUids instanceof Set) || !deadUids.size) return;
  db.projects.forEach(function (p) {
    if (!p || p.del || !p.appMeta || !Array.isArray(p.appMeta.predecessorUids)) return;
    const after = p.appMeta.predecessorUids.filter(function (uid) { return !deadUids.has(uid); });
    if (after.length === p.appMeta.predecessorUids.length) return;
    if (after.length) p.appMeta.predecessorUids = after;
    else delete p.appMeta.predecessorUids;
  });
}

function toggleProgrammeDependencies() {
  _predMapOpen = !_predMapOpen;
  renderOverview();
}
function _predMapText(value, max) {
  const s = String(value || '');
  return s.length > max ? s.slice(0, Math.max(1, max - 1)) + '…' : s;
}
function _programmeDependencyMapHtml() {
  if (!_predMapOpen) return '';
  const projects = _progProjects(true).filter(function (p) { return p && !p.del; });
  if (!projects.length) {
    return '<section class="pred-map-panel" id="programmeDependencyMap" aria-label="Programme map">'
      + '<div class="pred-map-head"><span class="pred-map-title">Programme map</span>'
      + '<span class="pred-map-note">Project predecessor relationships</span></div>'
      + '<div class="pred-map-empty">There are no projects to map yet.</div></section>';
  }
  const nodes = new Map();
  projects.forEach(function (p) {
    nodes.set(p.uid, { uid:p.uid, project:p, unavailable:false,
      label:p.name || 'Untitled project', meta:_dispId('PRJ', p) || 'Project' });
  });
  const edges = [], edgeSeen = new Set();
  projects.forEach(function (dep) {
    _projectPredecessorUids(dep).forEach(function (predUid) {
      if (!nodes.has(predUid)) {
        const known = _projectByUid(predUid);
        nodes.set(predUid, { uid:predUid, project:null, unavailable:true,
          label:'Unavailable project',
          meta:known ? 'Outside this programme' : _predMapText(predUid, 18) });
      }
      const key = predUid + '>' + dep.uid;
      if (!edgeSeen.has(key)) { edgeSeen.add(key); edges.push({ from:predUid, to:dep.uid }); }
    });
  });
  const indegree = new Map(), outgoing = new Map(), level = new Map();
  nodes.forEach(function (_n, uid) {
    indegree.set(uid, 0); outgoing.set(uid, []); level.set(uid, 0);
  });
  edges.forEach(function (edge) {
    if (!nodes.has(edge.from) || !nodes.has(edge.to)) return;
    outgoing.get(edge.from).push(edge.to);
    indegree.set(edge.to, indegree.get(edge.to) + 1);
  });
  const sortNode = function (a, b) {
    const A = nodes.get(a), B = nodes.get(b);
    const an = A.project ? _dispNoOf(A.project) : 0;
    const bn = B.project ? _dispNoOf(B.project) : 0;
    return an - bn || String(A.label).localeCompare(String(B.label))
      || String(a).localeCompare(String(b));
  };
  const queue = Array.from(nodes.keys())
    .filter(function (uid) { return indegree.get(uid) === 0; }).sort(sortNode);
  const placed = [];
  while (queue.length) {
    const uid = queue.shift();
    placed.push(uid);
    outgoing.get(uid).forEach(function (to) {
      level.set(to, Math.max(level.get(to), level.get(uid) + 1));
      indegree.set(to, indegree.get(to) - 1);
      if (indegree.get(to) === 0) { queue.push(to); queue.sort(sortNode); }
    });
  }
  const cycleUids = Array.from(nodes.keys())
    .filter(function (uid) { return placed.indexOf(uid) === -1; }).sort(sortNode);
  cycleUids.forEach(function (uid) { level.set(uid, 0); placed.push(uid); });
  const columns = new Map();
  placed.forEach(function (uid) {
    const column = level.get(uid) || 0;
    if (!columns.has(column)) columns.set(column, []);
    columns.get(column).push(uid);
  });
  columns.forEach(function (uids) { uids.sort(sortNode); });
  const maxLevel = Math.max.apply(null, Array.from(level.values()).concat([0]));
  const maxRows = Math.max.apply(null,
    Array.from(columns.values()).map(function (x) { return x.length; }).concat([1]));
  const nodeW = 184, nodeH = 56, colGap = 46, rowGap = 24, padX = 20, padY = 18;
  const width = Math.max(540, padX * 2 + (maxLevel + 1) * nodeW + maxLevel * colGap);
  const height = Math.max(104, padY * 2 + maxRows * nodeH + (maxRows - 1) * rowGap);
  const pos = new Map();
  columns.forEach(function (uids, column) {
    uids.forEach(function (uid, row) {
      pos.set(uid, { x:padX + column * (nodeW + colGap), y:padY + row * (nodeH + rowGap) });
    });
  });
  const edgeHtml = edges.map(function (edge) {
    const a = pos.get(edge.from), b = pos.get(edge.to);
    if (!a || !b) return '';
    const x1 = a.x + nodeW, y1 = a.y + nodeH / 2, x2 = b.x, y2 = b.y + nodeH / 2;
    const bend = Math.max(22, (x2 - x1) / 2);
    return '<path class="pred-map-edge" d="M' + x1 + ' ' + y1 + ' C'
      + (x1 + bend) + ' ' + y1 + ' ' + (x2 - bend) + ' ' + y2 + ' ' + x2 + ' ' + y2
      + '" marker-end="url(#predMapArrow)"><title>Predecessor relationship</title></path>';
  }).join('');
  const nodeHtml = placed.map(function (uid) {
    const n = nodes.get(uid), at = pos.get(uid), p = n.project;
    const classes = 'pred-map-node'
      + (p && p.archived ? ' pred-map-node-archived' : '')
      + (n.unavailable ? ' pred-map-node-unavailable' : '');
    const accessible = n.label + (p && p.archived ? ', archived' : '')
      + (n.unavailable ? ', unavailable' : '');
    const interactive = p
      ? ' role="button" tabindex="0" onclick="gotoProject(' + "'" + _jsInAttr(p.id) + "'" + ')"'
        + ' onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();this.click();}"'
      : '';
    return '<g class="' + classes + '" data-project-uid="' + _escapeHtml(uid) + '"'
      + interactive + ' aria-label="' + _escapeHtml(accessible) + '" transform="translate('
      + at.x + ' ' + at.y + ')"><title>' + _escapeHtml(accessible) + '</title>'
      + '<rect width="' + nodeW + '" height="' + nodeH + '" rx="8"></rect>'
      + '<text class="pred-map-node-id" x="12" y="19">' + _escapeHtml(_predMapText(n.meta, 25)) + '</text>'
      + '<text class="pred-map-node-name" x="12" y="39">' + _escapeHtml(_predMapText(n.label, 25)) + '</text></g>';
  }).join('');
  const warning = cycleUids.length
    ? '<div class="pred-map-warning">A stored dependency loop is present. Open the affected projects and remove one predecessor link.</div>'
    : '';
  return '<section class="pred-map-panel" id="programmeDependencyMap" aria-label="Programme map">'
    + '<div class="pred-map-head"><span class="pred-map-title">Programme map</span>'
    + '<span class="pred-map-note">Arrows run from predecessor to dependent project</span></div>'
    + warning + '<div class="pred-map-scroll"><svg class="pred-map-svg" role="img"'
    + ' aria-label="Project predecessor relationships" viewBox="0 0 ' + width + ' ' + height
    + '" width="' + width + '" height="' + height + '"><defs><marker id="predMapArrow"'
    + ' markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="strokeWidth">'
    + '<path class="pred-map-arrow" d="M0,0 L8,4 L0,8 z"></path></marker></defs>'
    + edgeHtml + nodeHtml + '</svg></div></section>';
}
/* ⚠ v12.3 (W2): `manager` is LAZY and must stay that way - _normProgrammes deliberately does
   NOT create it. Backfilling it would give every existing programme a key it never had and
   break the byte-exact round-trip on the very next save, which is why `archived` and `benSeq`
   are lazy too. The only writer is _managerEdit. */
function _normProgrammes() {
  const db = window.__DB__;
  if (!db || typeof db !== 'object') return;
  if (!Array.isArray(db.programmes)) db.programmes = [];
  db.programmes = db.programmes.filter(P => P && typeof P === 'object');
  const seen = new Set();
  db.programmes.forEach(P => {
    /* -- v17.3 (W2): A DELETION MARKER IS SKIPPED HERE, AND IT IS NOT TIDINESS ---------------
       Markers are still in this array at this moment, and every line below treats P as a live
       record. Running them over a marker did two measured kinds of damage, one in each possible
       array order:
         - MARKER FIRST: its id entered `seen`, so the LIVE programme sharing that id was
           re-minted to `delivery-2`. THAT MOVES A LIVE RECORD'S IDENTITY BECAUSE OF A DEAD ONE,
           which is exactly the harm v16.9's F3 exists to prevent; the display-number code states
           the same rule one function away ("tombstones never contest a number").
         - LIVE FIRST: the MARKER was re-minted instead, landing on the id `programme` (a marker
           carries no `name`, so the derivation falls back to its default). A deletion marker had
           been RELOCATED ONTO AN INNOCENT ID, where it would delete whatever real programme
           derived it. It was also given `name`, `rag` and `benefits` - the tombstone fattening
           the v16.7 W3 comment names one collection away, for projects.
       Both were measured on the released 17.2 shell before this line was written. A marker is
       identity plus `del` and nothing else: it is not shaped, not numbered, and it does not
       contest an id. It is deliberately NOT added to `seen` - live records contest ids with
       other live records only.
       The markers stay IN the array: `_normDisplayNumbers` below needs them for its high-water
       mark (v16.9 W4's `list`, not `live`), and `_revAdoptBaseline` harvests them shortly
       afterwards. Skipping the body is not the same as removing the record. */
    if (P.del) return;
    if (typeof P.id !== 'string' || !P.id || seen.has(P.id)) P.id = _newProgrammeId(P.name);
    seen.add(P.id);
    if (typeof P.name !== 'string') P.name = '';
    if (!P.rag || typeof P.rag !== 'object') P.rag = { level: null, rationale: '' };
    if (!['red', 'amber', 'green', null].includes(P.rag.level)) P.rag.level = null;
    if (typeof P.rag.rationale !== 'string') P.rag.rationale = '';
    /* v16.5 (f): an unrecognised lifecycle is DROPPED, not coerced - a value this app does not
       understand must not be shown as if it did. Lazy: a programme without one keeps no key. */
    if (P.lifecycle !== undefined && !PROG_LIFECYCLE.some(x => x[0] === P.lifecycle)) delete P.lifecycle;
    if (!Array.isArray(P.benefits)) P.benefits = [];
    P.benefits.forEach(_normBenefit);
  });
  /* ⚠ A project naming a programme that does not exist would be invisible in EVERY
     view — the v11 analogue of v10.0's silently-dropped benefit reference, but far
     worse, because it is the user's whole project. Adopt orphans into the first
     programme rather than hiding them. */
  /* ── ⚠⚠ v16.7 W3 (F2): THE GUARD ABOVE RAN ONE STEP TOO EARLY TO SEE THE CASE IT IS FOR ──
     `ids` was built from db.programmes AT THIS MOMENT — and at this moment the array still holds
     TOMBSTONES, because _revHarvestTombstones does not strip them until afterwards. So a programme
     a colleague DELETED still looked present, the orphan test found nothing to adopt, and the
     tombstone was removed a moment later — leaving the project pointing at a programme that no
     longer exists.
     ⚠ THE CONSEQUENCE IS THE ONE THIS GUARD'S OWN COMMENT NAMES: every listing filters projects by
     the active programme, and the portfolio lists programmes rather than projects, so such a
     project renders on NO SCREEN AT ALL. §v16.6 P1 reproduced it two ways — a colleague's newly
     created project, and (43% of 200 swept) an ordinary edited one that survived the delete on the
     merge tie-break. The data is still in the file; it is simply unreachable.
     ⚠ A TOMBSTONED PROJECT IS SKIPPED, and that is not tidiness: a deletion marker carries no
     programmeId, so the old test matched it and would have ASSIGNED one — fattening the tombstone,
     which is P1-F5's shape and would have written the extra key back to disk wholesale. */
  const liveProgs = db.programmes.filter(P => P && !P.del);
  const ids = new Set(liveProgs.map(P => P.id));
  /* ── ⚠⚠ v16.8 (W4): A PROJECT GOES WITH THE PROGRAMME THAT WAS DELETED ──────────────
     HIS RULING, and it overturns the adopt-the-orphan behaviour v16.7's W3 shipped: *"If you
     delete a program, the projects are deleted too. End of story."* — and, asked specifically
     about the case that arrives from a colleague's merge, *"Yes the project goes away always
     even in that edge case."*
     ⚠ THE DELETE PATH ALREADY DID THIS. `deleteProgrammeCascade` has removed the programme and
     its projects in one act, with one undo, since ◆A. So the only gap was the MERGE side: a
     colleague deletes the programme, my edited project wins the rev tie-break, and it arrives
     pointing at something gone — §v16.6 P1 measured that at 43% of 200. v16.7's W3 adopted it
     into the first surviving programme, and with NO programme surviving skipped the adoption
     entirely, leaving the project on no screen at all (SWEEP-2 finding 11). Cascading here
     answers both, and the "no programme survives" edge stops being a special case.
     ⚠⚠ A TOMBSTONE IS THE EVIDENCE, AND MERE ABSENCE IS NOT — THIS IS THE HALF THAT MATTERS.
     In a merge architecture "the programme is not here" is NOT the same fact as "the programme
     was deleted": a colleague's part may simply not have synced yet. Deleting on absence would
     destroy live work, which is the same reasoning that ruled out load-time pruning in W3.
     Only a `del` record proves a deletion — and tombstones are still in `db.programmes` at this
     point, which is precisely what v16.7's W3 comment above established. A project whose
     programme is merely UNKNOWN is still adopted, exactly as before.
     ⚠ NO TOMBSTONE IS MINTED FOR THE PROJECT, AND NONE IS NEEDED. Every step here is a pure
     function of the merged portfolio, so every machine computes the same answer from the same
     parts and converges without publishing anything — the v16.5 (d) self-heal idiom. The
     programme's own tombstone is what carries the fact between machines. */
  const deadProgIds = new Set(db.programmes.filter(P => P && P.del && typeof P.id === 'string').map(P => P.id));
  /* v17.1 W3: prune only with deletion evidence. A project tombstone proves its UID is
     gone; a programme tombstone proves the live project records it owns are about to be
     cascaded below. Mere absence is not proof, so unavailable UIDs from partial sync or
     subset Import remain untouched. */
  const deadProjectUids = new Set((Array.isArray(db.projects) ? db.projects : [])
    .filter(c => c && c.del && typeof c.uid === 'string').map(c => c.uid));
  if (deadProgIds.size && Array.isArray(db.projects)) {
    db.projects.forEach(function (c) {
      if (c && !c.del && deadProgIds.has(c.programmeId) && typeof c.uid === 'string')
        deadProjectUids.add(c.uid);
    });
  }
  _prunePredecessorUidsFromDB(db, deadProjectUids);
  if (Array.isArray(db.projects) && deadProgIds.size) {
    db.projects = db.projects.filter(c => !(c && !c.del && deadProgIds.has(c.programmeId)));
  }
  if (Array.isArray(db.projects) && liveProgs.length) {
    db.projects.forEach(c => {
      if (!c || c.del) return;   /* a deletion marker needs no home, and must not be given one */
      if (typeof c.programmeId !== 'string' || !ids.has(c.programmeId)) {
        c.programmeId = liveProgs[0].id;
      }
    });
  }
  if (!activeProgrammeId || !ids.has(activeProgrammeId)) {
    activeProgrammeId = liveProgs.length ? liveProgs[0].id : null;
  }
  _normDisplayNumbers(db);   /* v16.5 (d) */
}
/* ── ⚠⚠ v16.5 (d): THE SELF-HEAL THAT NUMBERS PROGRAMMES AND PROJECTS ─────────────────────
   HIS CONSTRAINT, and it shaped the whole design: *"I can't give you the js files and so you
   can't edit them directly. The app needs to self-heal them into a new version."* There is no
   migration he runs and no file he sends over — the app numbers his existing records ITSELF,
   the first time it opens them. The only thing he does is open the app.
   ⚠⚠ FILE ORDER IS THE NUMBERING RULE, AND IT IS NOT AN AESTHETIC CHOICE. This heal runs on
   EVERY machine independently — unlike v16.5 (a)'s heal, which only ever DELETES a
   contradictory key and so reaches the same answer trivially, this one CREATES values. Two
   colleagues opening the portfolio for the first time would otherwise number independently and
   DISAGREE: the same programme PRGM-01 on one screen and PRGM-02 on the other, with the merge
   then picking a winner per record and leaving gaps or duplicates in a numbering he intends to
   QUOTE. Array order is identical on every machine because every machine parsed the same bytes,
   so every machine mints the same numbers and the collision cannot arise. His call, delegated:
   "You decide."
   ⚠ STORED, NEVER DERIVED (◆d-1, his ruling). The registers' R-001 style is computed from
   position and shifts when the list changes; that is fine for a register read top-to-bottom in
   one sitting and WRONG for an id meant to be cited, because a number that moves is worse than
   no number at all. Once minted, a number never changes and is never recycled.
   ⚠ EXISTING NUMBERS ARE NEVER RE-MINTED, so this is idempotent and a fully-numbered portfolio
   is untouched — which is what keeps roundtrip_byte_exact honest on a file that already has them.
   ⚠ SILENT: no markDirty, matching _normalizeStakeholderIds and the (a) heal. Memory is correct
   immediately and the numbers land on whatever Save was coming anyway; the file is NEVER
   rewritten just to force it. It converges through normal use.
   ⚠ Projects are numbered ACROSS THE PORTFOLIO, not within a programme (◆d-3 recommendation,
   unvetoed): a project that moves between programmes must keep the number it was cited by.
   ⚠ A number already in use is respected even if it collides with the running counter, so a
   part merged from a colleague can never have its numbering rewritten by this pass. */
